import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { LanguageContext } from "../context/LanguageContext";
import { ThemeContext } from "../context/ThemeContext";
import { FaRobot, FaPaperPlane, FaSpinner, FaInfoCircle } from "react-icons/fa";
import "./EmailBot.css";

const EmailBot = () => {
  const { language: lang } = useContext(LanguageContext);
  const { theme } = useContext(ThemeContext);

  const [emailsText, setEmailsText] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  
  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  
  const logEndRef = useRef(null);

  useEffect(() => {
    // Scroll to bottom of logs when updated
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs]);

  const addLog = (msg, type = "system") => {
    setLogs((prev) => [...prev, { id: Date.now() + Math.random(), msg, type }]);
  };

  const handleStart = async () => {
    const emailList = emailsText
      .split('\n')
      .map(e => e.trim())
      .filter(e => e !== '');

    if (emailList.length === 0 || !subject || !message) {
      alert(lang === "ar" ? "يرجى تعبئة جميع الحقول!" : "Please fill all fields!");
      return;
    }

    // Get admin ID
    const storedData = localStorage.getItem("userData") || sessionStorage.getItem("userData");
    if (!storedData) {
      alert(lang === "ar" ? "غير مصرح لك!" : "Unauthorized!");
      return;
    }
    const adminId = JSON.parse(storedData).id;

    setIsSending(true);
    setProgress(0);
    setLogs([]);
    addLog(lang === "ar" ? "🚀 بدء تشغيل بوت الإرسال..." : "🚀 Starting Sender Bot...", "system");

    let successCount = 0;
    const total = emailList.length;

    for (let i = 0; i < total; i++) {
      const currentEntry = emailList[i];
      addLog(`[${i + 1}/${total}] ${lang === "ar" ? "جاري الإرسال إلى:" : "Sending to:"} ${currentEntry}...`, "system");

      try {
        const response = await axios.post("/api/send_email_bot.php", {
          admin_id: adminId,
          email: currentEntry,
          subject: subject,
          message: message
        });

        if (response.data.status === "success") {
          addLog(`✅ ${lang === "ar" ? "تم الإرسال بنجاح" : "Successfully sent"}: ${response.data.message}`, "success");
          successCount++;
        } else {
          addLog(`❌ ${lang === "ar" ? "فشل" : "Failed"}: ${response.data.message}`, "error");
        }
      } catch (error) {
        const errMessage = error.response?.data?.message || error.message;
        addLog(`❌ ${lang === "ar" ? "خطأ في الاتصال" : "Connection Error"}: ${errMessage}`, "error");
      }

      const percent = Math.round(((i + 1) / total) * 100);
      setProgress(percent);

      // Wait 5 seconds between emails (except for the last one) to prevent SMTP bans
      if (i < total - 1) {
        addLog(`⏳ ${lang === "ar" ? "انتظار 5 ثوانٍ لتجنب الحظر..." : "Waiting 5 seconds to avoid bans..."}`, "system");
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    }

    addLog(`🎉 ${lang === "ar" ? "اكتملت العملية!" : "Process Complete!"} ${successCount}/${total} ${lang === "ar" ? "ناجح" : "successful"}.`, "system");
    setIsSending(false);
  };

  return (
    <div className={`email-bot-page ${theme}`}>
      <div className="bot-container">
        
        <div className="bot-header">
          <h2><FaRobot /> {lang === "ar" ? "أداة الإيميلات الذكية" : "Smart Email Bot"}</h2>
          <p>{lang === "ar" ? "قم بإرسال رسائل مخصصة وتلقائية للعملاء بسهولة." : "Send personalized, automated emails to clients easily."}</p>
        </div>

        <div className="form-group">
          <label>
            {lang === "ar" ? "قائمة الإيميلات (كل عميل في سطر)" : "Email List (One per line)"}
          </label>
          <p className="hint-text">
            <FaInfoCircle /> 
            {lang === "ar" 
              ? "يمكنك كتابة الإيميل فقط، أو الإيميل والاسم لنتائج أدق (مثال: ahmed@gmail.com, أحمد خالد)" 
              : "You can write just the email, or email and name for precise results (e.g., ahmed@gmail.com, Ahmed Khaled)"}
          </p>
          <textarea 
            rows="5" 
            placeholder={lang === "ar" ? "ahmed@gmail.com, أحمد خالد\nsara@yahoo.com" : "ahmed@gmail.com, Ahmed Khaled\nsara@yahoo.com"}
            value={emailsText}
            onChange={(e) => setEmailsText(e.target.value)}
            disabled={isSending}
          />
        </div>

        <div className="form-group">
          <label>{lang === "ar" ? "عنوان الإيميل (Subject)" : "Email Subject"}</label>
          <input 
            type="text" 
            placeholder={lang === "ar" ? "عرض خاص لخدماتنا..." : "Special offer for our services..."}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={isSending}
          />
        </div>

        <div className="form-group">
          <label>{lang === "ar" ? "محتوى الرسالة" : "Message Content"}</label>
          <p className="hint-text">
            <FaInfoCircle />
            {lang === "ar" 
              ? "استخدم الكلمة [Name] في المكان الذي تريد أن يظهر فيه اسم العميل الذكي." 
              : "Use [Name] where you want the smart extracted name to appear."}
          </p>
          <textarea 
            rows="6" 
            placeholder={lang === "ar" ? "أهلاً بك أستاذ [Name]،\nنحن في Codical نقدم لك..." : "Hello [Name],\nAt Codical we offer..."}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={isSending}
          />
        </div>

        <button 
          className="btn-send" 
          onClick={handleStart}
          disabled={isSending || !emailsText.trim() || !subject.trim() || !message.trim()}
        >
          {isSending ? (
            <><FaSpinner className="spin" /> {lang === "ar" ? "جاري الإرسال..." : "Sending..."}</>
          ) : (
            <><FaPaperPlane /> {lang === "ar" ? "ابدأ الإرسال التلقائي" : "Start Automated Sending"}</>
          )}
        </button>

        <div className="progress-container">
          <div className="progress-label">
            <span>{lang === "ar" ? "نسبة التقدم:" : "Progress:"}</span>
            <span>{progress}%</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        </div>

        <div className="log-terminal">
          {logs.length === 0 ? (
            <span className="log-entry system">{lang === "ar" ? "المنصة جاهزة لبدء الإرسال..." : "Platform ready to send..."}</span>
          ) : (
            logs.map(log => (
              <div key={log.id} className={`log-entry ${log.type}`}>
                {log.msg}
              </div>
            ))
          )}
          <div ref={logEndRef} />
        </div>

      </div>
    </div>
  );
};

export default EmailBot;
